import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

/**
 * Hook: propostas de uma oferta com nested selects via PostgREST.
 *
 * Modulo Imoveis Fase B — 1 oferta : N propostas : N imoveis : N proprietarios.
 *
 * FKs usadas (todas com nomes padrao `<tabela>_<coluna>_fkey`):
 *   propostas.oferta_id → ofertas
 *   propostas.proponente_empresa_id → empresas (desambiguar)
 *   proposta_imoveis.proposta_id → propostas
 *   proposta_imoveis.imovel_id → imoveis
 *   imoveis.lote_id → lotes
 *   imovel_proprietarios.imovel_id → imoveis
 *   imovel_proprietarios.proprietario_empresa_id → empresas
 *   imovel_proprietarios.proprietario_contato_id → contatos
 */
export const usePropostasOferta = (ofertaId) => {
  const [propostas, setPropostas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    if (!ofertaId) { setPropostas([]); return; }
    setLoading(true);
    setError(null);
    try {
      const { data, error: qErr } = await supabase
        .from('propostas')
        .select(`
          *,
          proponente:empresas!propostas_proponente_empresa_id_fkey(id, empresa, company_name),
          proposta_imoveis(
            id, valor_especifico, area_especifica_m2, notas,
            imovel:imoveis(
              id, nome, area_terreno_m2, situacao, matricula,
              lote:lotes(id, nome, zoneamento),
              proprietarios:imovel_proprietarios(
                id, percentual_participacao, tipo_propriedade,
                empresa:empresas!imovel_proprietarios_proprietario_empresa_id_fkey(id, empresa, company_name),
                contato:contatos!imovel_proprietarios_proprietario_contato_id_fkey(id, first_name, last_name)
              )
            )
          )
        `)
        .eq('oferta_id', ofertaId)
        .order('created_at', { ascending: false });

      if (qErr) throw qErr;
      setPropostas(data || []);
    } catch (err) {
      console.error('[usePropostasOferta] fetch falhou:', err);
      setError(err.message);
      setPropostas([]);
    } finally {
      setLoading(false);
    }
  }, [ofertaId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const createProposta = async (payload) => {
    const { data, error: err } = await supabase
      .from('propostas')
      .insert({ ...payload, oferta_id: ofertaId })
      .select()
      .single();
    if (err) throw err;
    await fetchAll();
    return data;
  };

  const updateProposta = async (id, updates) => {
    const { error: err } = await supabase
      .from('propostas')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (err) throw err;
    await fetchAll();
  };

  return { propostas, loading, error, refetch: fetchAll, createProposta, updateProposta };
};
